import { membershipNameType } from './constant'

/**
 * Shared logic for the "maayot Weekly" exclusive offer aimed at a subset of free learners.
 *
 * A learner is in the cohort when the backend flagged them (`users.weekly_test_cohort`),
 * surfaced on the profile payload as `credentials.customFields.weekly_test_group`:
 *   'none'    -> not in the cohort
 *   'emailed' -> in the cohort, received the offer email
 *   'holdout' -> in the cohort, no email but still has access
 * So any value other than 'none' means the learner has exclusive access to the Weekly plan.
 *
 * Timeline: until WEEKLY_HARD_GATE_AT the homepage paywall is a dismissible nudge. From that
 * instant on it becomes a hard gate that only a paid plan can clear.
 */

// The "regular" yearly list price the exclusive offer is discounted from (USD).
export const WEEKLY_REGULAR_YEARLY_PRICE = 60

// When the homepage paywall switches from "dismissible nudge" to "hard gate".
// Anchored to Hong Kong time (UTC+8), matching the rest of the app's story cadence.
export const WEEKLY_HARD_GATE_AT = new Date('2026-06-19T00:00:00+08:00')

export const getWeeklyTestGroup = (credentials: any): string =>
  (credentials?.customFields as any)?.weekly_test_group || 'none'

export const isWeeklyCohortMember = (credentials: any): boolean => {
  const group = getWeeklyTestGroup(credentials)
  return Boolean(group) && group !== 'none'
}

const isFreeMembership = (membership: any): boolean => membership?.name === membershipNameType.Free

/**
 * The audience for the offer / paywall: a free learner who is in the Weekly cohort.
 * Learners already on a paid plan (incl. Weekly) are never gated.
 */
export const isWeeklyOfferAudience = (membership: any, credentials: any): boolean =>
  isFreeMembership(membership) && isWeeklyCohortMember(credentials)

export const isWeeklyHardGateActive = (now: Date = new Date()): boolean =>
  now.getTime() >= WEEKLY_HARD_GATE_AT.getTime()

/**
 * Once the hard gate is live, free learners in the Weekly cohort no longer get free story
 * access (not even the Sunday free read) — they must subscribe. This is the backstop used by
 * the story-open handlers: if such a learner gets past the on-page paywall overlay and clicks a
 * story, we send them to the plan page instead of opening it. Paid plans (including Weekly
 * subscribers) and free learners outside the cohort are never blocked here.
 */
export const isWeeklyReadingBlocked = (
  membership: any,
  credentials: any,
  now: Date = new Date(),
): boolean => isWeeklyOfferAudience(membership, credentials) && isWeeklyHardGateActive(now)

/** Whole-number discount % of the exclusive yearly price vs the regular $60/yr. */
export const getWeeklyDiscountPercent = (exclusiveYearly: number): number => {
  if (!exclusiveYearly || exclusiveYearly >= WEEKLY_REGULAR_YEARLY_PRICE) return 0
  return Math.round(((WEEKLY_REGULAR_YEARLY_PRICE - exclusiveYearly) / WEEKLY_REGULAR_YEARLY_PRICE) * 100)
}

// sessionStorage key so a dismissed nudge stays dismissed for the browsing session
// (it re-appears on a fresh visit). Ignored entirely once the hard gate is active.
export const WEEKLY_GATE_DISMISS_KEY = 'maayot_weekly_gate_dismissed_v1'
